import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AsyncStripeProvider from '../../components/AsyncStripeProvider';

const wrap = { maxWidth: 480, margin: '0 auto', padding: '28px 20px', fontFamily: 'Arial, Helvetica, sans-serif', color: '#2b2b2b' };
const card = { border: '1px solid #eee', borderRadius: 14, padding: '22px 20px', boxShadow: '0 6px 24px rgba(0,0,0,0.06)' };
const btn = { display: 'block', width: '100%', background: '#16a34a', color: '#fff', border: 0, borderRadius: 10, padding: '14px 16px', fontSize: 16, fontWeight: 'bold', cursor: 'pointer', marginTop: 16 };
const btnGhost = { ...btn, background: 'transparent', color: '#16a34a', border: '1px solid #16a34a', boxShadow: 'none' };
const rowAmt = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0', fontSize: 15 };
const brandBar = { textAlign: 'center', paddingBottom: 18, borderBottom: '3px solid #E8232B', marginBottom: 20 };
const CARD_OPTS = { style: { base: { fontSize: '16px', color: '#2b2b2b', '::placeholder': { color: '#9aa0a6' } } } };

function postConfirm(token, paymentMethod) {
  const query = `mutation($token:String,$paymentMethod:String){ confirmClientCard(token:$token,paymentMethod:$paymentMethod){ status errorMessage bookingId requiresAction clientSecret } }`;
  return fetch('/graphql', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ query, variables: { token, paymentMethod: paymentMethod || null } }),
  }).then(r => r.json()).then(j => (j && j.data && j.data.confirmClientCard) || { status: 400, errorMessage: 'Network error' });
}

function PayForm({ data, token, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [useNew, setUseNew] = useState(!data.hasCard);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const amt = `${data.currency || '$'}${Number(data.amount || 0).toFixed(2)}`;

  async function submit(withNewCard) {
    setErr(null); setBusy(true);
    try {
      let paymentMethod = null;
      if (withNewCard) {
        if (!stripe || !elements) { setErr('Payment form still loading, please wait…'); setBusy(false); return; }
        const { error, paymentMethod: pm } = await stripe.createPaymentMethod({ type: 'card', card: elements.getElement(CardElement) });
        if (error) { setErr(error.message); setBusy(false); return; }
        paymentMethod = pm.id;
      }
      let res = await postConfirm(token, paymentMethod);
      if (res.requiresAction && res.clientSecret && stripe) {
        const conf = await stripe.confirmCardPayment(res.clientSecret);
        if (conf.error) { setErr(conf.error.message); setBusy(false); return; }
        res = await postConfirm(token, null);
      }
      if (res.status === 200 && !res.requiresAction) { onDone(); return; }
      setErr(res.errorMessage || 'Could not confirm. Please try again.');
    } catch (e) { setErr('Something went wrong. Please try again.'); }
    setBusy(false);
  }

  return (
    <div>
      {data.hasCard && !useNew ? (
        <div>
          <div style={{ fontSize: 14, color: '#555', marginBottom: 6 }}>Pay with your saved card</div>
          <div style={{ padding: '12px 14px', border: '1px solid #eee', borderRadius: 10, fontWeight: 'bold' }}>
            {(data.cardBrand || 'Card').toUpperCase()} •••• {data.cardLast4}
          </div>
          <button style={btn} disabled={busy} onClick={() => submit(false)}>{busy ? 'Confirming…' : `Confirm & authorize ${amt}`}</button>
          <button style={btnGhost} disabled={busy} onClick={() => setUseNew(true)}>Use a different card</button>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>Enter your card</div>
          <div style={{ padding: '14px', border: '1px solid #ddd', borderRadius: 10 }}><CardElement options={CARD_OPTS} /></div>
          <button style={btn} disabled={busy} onClick={() => submit(true)}>{busy ? 'Confirming…' : `Add card & authorize ${amt}`}</button>
          {data.hasCard ? <button style={btnGhost} disabled={busy} onClick={() => setUseNew(false)}>Use my saved card instead</button> : null}
        </div>
      )}
      {err ? <div style={{ color: '#c0392b', marginTop: 12, fontSize: 14 }}>{err}</div> : null}
      <div style={{ color: '#8a8a8a', fontSize: 12, marginTop: 16, textAlign: 'center' }}>
        Your card is entered securely with Stripe and is only <strong>authorized</strong> now — you're charged after the job is completed.
      </div>
    </div>
  );
}

function CompletePayment({ data, token }) {
  const [done, setDone] = useState(false);

  const Shell = ({ children }) => (
    <div style={wrap}><div style={card}>
      <div style={brandBar}><img src="https://www.besthandapp.com/api/images/logo/93d537042aafaa972d06cae1a9243ef7.png" alt="BestHand" style={{ height: 34 }} /></div>
      {children}
    </div></div>
  );

  if (!data || data.state === 'invalid') return <Shell><h2 style={{ marginTop: 0 }}>Link not valid</h2><p>This confirmation link is invalid. Please contact BestHand support.</p></Shell>;
  if (data.state === 'expired') return <Shell><h2 style={{ marginTop: 0 }}>Link expired</h2><p>This link has expired. Please ask BestHand to resend your confirmation link.</p></Shell>;
  if (data.state === 'already') return <Shell><h2 style={{ marginTop: 0 }}>Already handled</h2><p>This booking has already been confirmed or is no longer pending.</p></Shell>;
  if (done) return <Shell><h2 style={{ marginTop: 0, color: '#16a34a' }}>You're all set! 🎉</h2><p>Your booking is confirmed. We're matching you with a pro now — you'll hear from us shortly.</p></Shell>;

  const amt = `${data.currency || '$'}${Number(data.amount || 0).toFixed(2)}`;
  return (
    <Shell>
      <h2 style={{ marginTop: 0 }}>Confirm your booking</h2>
      <p style={{ color: '#555', marginTop: 0 }}>Hi {data.firstName || 'there'}, please review and authorize your booking.</p>
      <div style={{ background: '#fafafa', borderRadius: 10, padding: '14px 16px', margin: '14px 0 20px' }}>
        {data.serviceName ? <div style={rowAmt}><span style={{ color: '#666' }}>Service</span><strong>{data.serviceName}</strong></div> : null}
        {data.location ? <div style={rowAmt}><span style={{ color: '#666' }}>Location</span><span style={{ textAlign: 'right', maxWidth: 240 }}>{data.location}</span></div> : null}
        <div style={{ ...rowAmt, borderTop: '1px solid #eee', paddingTop: 10, marginTop: 8 }}><span style={{ color: '#666' }}>Total (authorized)</span><strong style={{ fontSize: 18 }}>{amt}</strong></div>
      </div>
      <AsyncStripeProvider apiKey={data.publishableKey}>
        <PayForm data={data} token={token} onDone={() => setDone(true)} />
      </AsyncStripeProvider>
    </Shell>
  );
}

export default CompletePayment;
